import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type CSSProperties,
} from 'react'
import { ConfigProvider } from 'antd'
import { useTranslation } from 'react-i18next'
import type { IconBaseProps } from 'react-icons'
import {
  MdSearch as MdSearchRaw,
  MdChevronRight as MdChevronRightRaw,
  MdChevronLeft as MdChevronLeftRaw,
  MdEdit as MdEditRaw,
  MdGTranslate as MdGTranslateRaw,
  MdShield as MdShieldRaw,
  MdPerson as MdPersonRaw,
  MdHeadsetMic as MdHeadsetMicRaw,
} from 'react-icons/md'

import './Settings.scss'
import SearchInput from '../../components/SearchInput/SearchInput'

import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import { logout, attemptGetMe, type User } from '../../utils/redux/authSlice'
import { setLanguage, setUserSelectedTheme, THEME_ID } from '../../utils/redux/appSlice'
import { getAntdTheme } from '../../utils/theme/antdThemeConfig'
import {
  LANGUAGE_OPTIONS,
  ALLOWED_LANGUAGES,
  DEFAULT_LANGUAGE,
  type Lang,
} from '../../utils/i18n'
import TermsFR from '../legal/TermsFR'
import { COUNTRY } from '../../utils/scaling'
import TermsUA from '../legal/TermsUA'


/** FIX TS2786 для react-icons */
const MdSearch = MdSearchRaw as unknown as FC<IconBaseProps>
const MdChevronRight = MdChevronRightRaw as unknown as FC<IconBaseProps>
const MdChevronLeft = MdChevronLeftRaw as unknown as FC<IconBaseProps>
const MdEdit = MdEditRaw as unknown as FC<IconBaseProps>
const MdGTranslate = MdGTranslateRaw as unknown as FC<IconBaseProps>
const MdShield = MdShieldRaw as unknown as FC<IconBaseProps>
const MdPerson = MdPersonRaw as unknown as FC<IconBaseProps>
const MdHeadsetMic = MdHeadsetMicRaw as unknown as FC<IconBaseProps>

type InfoKey = 'confidential' | 'account' | 'contacts'

function getLangCode(raw: string): string {
  return String(raw || 'en').split('-')[0]
}

function syncThemeToDom(themeId: THEME_ID) {
  const root = document.documentElement
  const body = document.body

  const apply = (isDark: boolean) => {
    const next = isDark ? 'dark' : 'light'

    root.dataset.theme = next
    body?.setAttribute('data-theme', next)

    root.classList.toggle('theme-dark', isDark)
    root.classList.toggle('theme-light', !isDark)

    body?.classList.toggle('theme-dark', isDark)
    body?.classList.toggle('theme-light', !isDark)

    root.style.colorScheme = isDark ? 'dark' : 'light'
  }

  if (themeId === THEME_ID.DARK) {
    apply(true)
    return () => {}
  }

  if (themeId === THEME_ID.LIGHT) {
    apply(false)
    return () => {}
  }

  const mql = window.matchMedia?.('(prefers-color-scheme: dark)')
  if (!mql) {
    apply(true)
    return () => {}
  }

  apply(mql.matches)

  const onChange = (e: MediaQueryListEvent) => apply(e.matches)

  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }

  // Safari fallback
  // @ts-ignore
  mql.addListener(onChange)
  // @ts-ignore
  return () => mql.removeListener(onChange)
}

function decodeJwtPayload(token?: string): Record<string, any> | null {
  if (!token) return null
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null

    const payload = parts[1]
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const pad = base64.length % 4
    if (pad) base64 += '='.repeat(4 - pad)

    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    )

    return JSON.parse(json)
  } catch {
    return null
  }
}

function normalizeTs(raw: unknown): number | null {
  const n = typeof raw === 'string' ? Number(raw) : typeof raw === 'number' ? raw : NaN
  if (!Number.isFinite(n) || n <= 0) return null
  return n < 1e12 ? n * 1000 : n
}

function toLocaleFromLang(lang: string) {
  const code = String(lang || 'en').split('-')[0]
  if (code === 'uk') return 'uk-UA'
  if (code === 'fr') return 'fr-FR'
  if (code === 'ru') return 'ru-RU'
  return 'en-GB'
}

export default function Settings() {
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation('settings')
  const tr = (key: string, def: string) => t(key, { defaultValue: def })

  const themeId = useAppSelector((s: any) => s.app?.themeId) as THEME_ID

  // auth state
  const authUser = useAppSelector((s: any) => s.auth?.user as User | undefined)
  const authToken = useAppSelector((s: any) => s.auth?.token as string | undefined)
  const profileLoading = useAppSelector((s: any) => Boolean(s.auth?.profileLoading))

  // dropdowns
  const [themeOpen, setThemeOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)

  // general info panel
  const [activeInfo, setActiveInfo] = useState<InfoKey | null>(null)
  const [detailsTop, setDetailsTop] = useState(0)

  // responsive
  const [isMobile, setIsMobile] = useState(false)
  const [devicePrefersDark, setDevicePrefersDark] = useState(true)

  // anchors / popovers
  const themeAnchorRef = useRef<HTMLButtonElement | null>(null)
  const themePopoverRef = useRef<HTMLDivElement | null>(null)
  const langAnchorRef = useRef<HTMLButtonElement | null>(null)
  const langPopoverRef = useRef<HTMLDivElement | null>(null)

  // left column + info row refs
  const leftColRef = useRef<HTMLDivElement | null>(null)
  const confRef = useRef<HTMLButtonElement | null>(null)
  const accRef = useRef<HTMLButtonElement | null>(null)
  const contRef = useRef<HTMLButtonElement | null>(null)

  // --- SAFE SPACE ПОД ГЛОБАЛЬНЫЙ ПЛЕЕР ---
  const PLAYER_SAFE_SPACE_DESKTOP = 126
  const PLAYER_SAFE_SPACE_MOBILE = 102
  const playerSafeSpace = isMobile ? PLAYER_SAFE_SPACE_MOBILE : PLAYER_SAFE_SPACE_DESKTOP

  const pageStyle = useMemo<CSSProperties>(
    () => ({
      paddingBottom: playerSafeSpace,
    }),
    [playerSafeSpace],
  )

  const shouldPinTop = activeInfo === 'account'

  const rightFloatStyle = useMemo<CSSProperties>(
    () => ({
      transform: shouldPinTop ? 'translateY(0px)' : `translateY(${detailsTop}px)`,
      marginBottom: PLAYER_SAFE_SPACE_DESKTOP,
    }),
    [detailsTop, shouldPinTop],
  )

  const mobileOverlayStyle = useMemo<CSSProperties>(
    () => ({
      zIndex: 9999,
      paddingBottom: `calc(${PLAYER_SAFE_SPACE_MOBILE}px + env(safe-area-inset-bottom))`,
    }),
    [],
  )

  const mobileBodyStyle = useMemo<CSSProperties>(
    () => ({
      paddingBottom: PLAYER_SAFE_SPACE_MOBILE,
    }),
    [],
  )

  // detect mobile
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)')
    const onChange = () => setIsMobile(mq.matches)
    onChange()

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }

    // Safari fallback
    // @ts-ignore
    mq.addListener(onChange)
    // @ts-ignore
    return () => mq.removeListener(onChange)
  }, [])

  // detect OS theme (for AS_DEVICE)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setDevicePrefersDark(mq.matches)
    onChange()

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }

    // Safari fallback
    // @ts-ignore
    mq.addListener(onChange)
    // @ts-ignore
    return () => mq.removeListener(onChange)
  }, [])

  // resolved theme for antd on Settings page
  const resolvedThemeId = useMemo<THEME_ID>(() => {
    const current = themeId ?? THEME_ID.DARK
    if (current === THEME_ID.AS_DEVICE) {
      return devicePrefersDark ? THEME_ID.DARK : THEME_ID.LIGHT
    }
    return current
  }, [themeId, devicePrefersDark])

  const settingsAntdTheme = useMemo(
    () => getAntdTheme(resolvedThemeId),
    [resolvedThemeId],
  )

  // enforce allowed languages for current build
  useEffect(() => {
    const code = getLangCode(i18n.language) as Lang
    if (!ALLOWED_LANGUAGES.includes(code)) {
      void i18n.changeLanguage(DEFAULT_LANGUAGE)
    }
  }, [i18n.language, i18n])

  // apply theme to DOM
  useEffect(() => {
    if (typeof window === 'undefined') return
    return syncThemeToDom(themeId ?? THEME_ID.DARK)
  }, [themeId])

  // fetch profile if token exists and user empty
  useEffect(() => {
    if (!authToken || authUser || profileLoading) return
    void dispatch(attemptGetMe())
  }, [authToken, authUser, profileLoading, dispatch])

  // if user opens "Account Info", try load again
  useEffect(() => {
    if (activeInfo !== 'account') return
    if (!authToken || authUser || profileLoading) return
    void dispatch(attemptGetMe())
  }, [activeInfo, authToken, authUser, profileLoading, dispatch])

  const langCode = getLangCode(i18n.language) as Lang

  const languageLabel = useMemo(() => {
    const found = LANGUAGE_OPTIONS.find((x) => x.value === langCode)
    return found?.label ?? langCode.toUpperCase()
  }, [langCode])

  const themeLabel = useMemo(() => {
    const current = themeId ?? THEME_ID.DARK
    if (current === THEME_ID.DARK) return tr('themeDark', 'Dark')
    if (current === THEME_ID.LIGHT) return tr('themeLight', 'Light')
    return tr('themeDevice', 'Follow to device')
  }, [themeId, i18n.language])

  const infoTitle = useMemo(() => {
    if (activeInfo === 'confidential') {
      return tr('confidentialityAgreement', 'Confidentiality Agreement')
    }
    if (activeInfo === 'account') return tr('accountInfo', 'Account Info')
    if (activeInfo === 'contacts') return tr('contacts', 'Contacts')
    return ''
  }, [activeInfo, i18n.language])

  // close popovers on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const tnode = e.target as Node

      if (themeOpen) {
        const a = themeAnchorRef.current
        const p = themePopoverRef.current
        if (a && a.contains(tnode)) return
        if (p && p.contains(tnode)) return
        setThemeOpen(false)
      }

      if (langOpen) {
        const a = langAnchorRef.current
        const p = langPopoverRef.current
        if (a && a.contains(tnode)) return
        if (p && p.contains(tnode)) return
        setLangOpen(false)
      }
    }

    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [themeOpen, langOpen])

  // ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setThemeOpen(false)
      setLangOpen(false)
      setActiveInfo(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const applyTheme = (next: THEME_ID) => {
    setThemeOpen(false)
    dispatch(setUserSelectedTheme(next))
  }

  const LANG_STORAGE_KEY = 'i18nextLng'

  const applyLang = (next: Lang) => {
    if (!ALLOWED_LANGUAGES.includes(next)) return
    setLangOpen(false)

    try {
      localStorage.setItem(LANG_STORAGE_KEY, next)
    } catch {}

    void i18n.changeLanguage(next)
    dispatch(setLanguage(next))
  }

  // align right content with selected left row
  const calcDetailsTop = () => {
    if (!activeInfo) {
      setDetailsTop(0)
      return
    }

    // Account Info всегда сверху
    if (activeInfo === 'account') {
      setDetailsTop(0)
      return
    }

    const left = leftColRef.current
    if (!left) return

    const leftRect = left.getBoundingClientRect()

    // Здесь activeInfo уже только 'confidential' | 'contacts'
    const row = activeInfo === 'confidential' ? confRef.current : contRef.current
    if (!row) return

    const rowRect = row.getBoundingClientRect()
    const top = Math.max(0, rowRect.top - leftRect.top - 6)
    setDetailsTop(top)
  }

  useEffect(() => {
    calcDetailsTop()
    window.addEventListener('resize', calcDetailsTop)
    return () => window.removeEventListener('resize', calcDetailsTop)
  }, [activeInfo])

  useEffect(() => {
    calcDetailsTop()
  }, [themeOpen, langOpen])

  const Row = (props: {
    labelTop?: string
    value: string
    mobileTitle?: string
    mobileRight?: string
    active?: boolean
    onClick?: () => void
    buttonRef?: React.Ref<HTMLButtonElement>
    chevronAccent?: boolean
    IconMobile?: FC<IconBaseProps>
  }) => {
    const {
      labelTop,
      value,
      mobileTitle,
      mobileRight,
      active,
      onClick,
      buttonRef,
      chevronAccent,
      IconMobile = MdSearch,
    } = props

    return (
      <div className="stRowWrap">
        {labelTop ? (
          <div className="stRowLabel">{labelTop}</div>
        ) : (
          <div className="stRowLabel stRowLabel--empty" />
        )}

        <button
          ref={buttonRef}
          type="button"
          className={`stRow ${active ? 'isActive' : ''} ${chevronAccent ? 'isChevronAccent' : ''}`}
          onClick={onClick}
        >
          <div className="stRowLeft">
            <span className="stRowIcon stRowIconDesktop" aria-hidden="true">
              <MdSearch size={16} />
            </span>

            <span className="stRowIcon stRowIconMobile" aria-hidden="true">
              <IconMobile size={18} />
            </span>

            <span className="stRowTextDesktop">{value}</span>
            <span className="stRowTextMobile">{mobileTitle ?? value}</span>
          </div>

          <div className="stRowRight">
            {mobileRight ? (
              <span className="stRowRightValue" aria-hidden="true">
                {mobileRight}
              </span>
            ) : null}

            <span className="stRowChevron" aria-hidden="true">
              <MdChevronRight size={18} />
            </span>
          </div>
        </button>
      </div>
    )
  }

  const ContactsPanel = () => {
    const [dept, setDept] = useState<'marketing' | 'tech'>('marketing')
    const [msg, setMsg] = useState('')

    return (
      <div className="stContactCard">
        <div className="stContactTitle">{tr('sendTo', 'Send to')}</div>

        <div className="stRadioList">
          <label className="stRadioLine">
            <span className="stRadioText">
              {tr('marketingDepartment', 'Marketing Department')}
            </span>
            <input
              type="radio"
              name="dept"
              checked={dept === 'marketing'}
              onChange={() => setDept('marketing')}
            />
            <span className="stRadioUi" />
          </label>

          <label className="stRadioLine">
            <span className="stRadioText">
              {tr('technicalDepartment', 'Technical Department')}
            </span>
            <input
              type="radio"
              name="dept"
              checked={dept === 'tech'}
              onChange={() => setDept('tech')}
            />
            <span className="stRadioUi" />
          </label>
        </div>

        <textarea
          className="stTextarea"
          placeholder={tr('writeMessageHere', 'Write your message here')}
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
        />

        <button type="button" className="stSendBtn">
          {tr('sendMessage', 'Send message')}
        </button>
      </div>
    )
  }

  const toggleInfo = (key: InfoKey) => {
    setThemeOpen(false)
    setLangOpen(false)
    setActiveInfo((prev) => (prev === key ? null : key))
  }

  const themeClass =
    resolvedThemeId === THEME_ID.LIGHT ? 'settingsPage--light' : 'settingsPage--dark'

  // fallback user info from JWT payload when auth.user is still empty
  const tokenPayload = useMemo(() => decodeJwtPayload(authToken), [authToken])

  const accountUser = useMemo(() => {
    if (authUser) return authUser
    if (!tokenPayload) return undefined

    return {
      email: String(tokenPayload.email ?? tokenPayload.sub ?? ''),
      password: '',
      name: String(tokenPayload.name ?? tokenPayload.username ?? ''),
      phone: String(tokenPayload.phone ?? ''),
      identifier: String(
        tokenPayload.identifier ?? tokenPayload.userId ?? tokenPayload.id ?? '',
      ),
      registeredAt: Number(tokenPayload.registeredAt ?? tokenPayload.iat ?? 0),
      validatedAt: Number(tokenPayload.validatedAt ?? 0) || undefined,
      bannedAt: Number(tokenPayload.bannedAt ?? 0) || undefined,
    } as User
  }, [authUser, tokenPayload])

  const accountLocale = useMemo(
    () => toLocaleFromLang(i18n.resolvedLanguage || i18n.language || 'en'),
    [i18n.resolvedLanguage, i18n.language],
  )

  const formatAccountDate = (raw: unknown) => {
    const ms = normalizeTs(raw)
    if (!ms) return '—'
    const d = new Date(ms)
    if (Number.isNaN(d.getTime())) return '—'
    return new Intl.DateTimeFormat(accountLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d)
  }

  const getInitials = (name?: string, email?: string) => {
    const base = String(name || email || '').trim()
    if (!base) return 'U'

    const parts = base.split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()

    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
  }

  const AccountInfoPanel = () => {
    if (profileLoading && !accountUser) {
      return (
        <div className="stAccountCard">
          <div className="stAccountHead">
            <div className="stAccountAvatar stSkeleton" />
            <div className="stAccountHeadText">
              <div className="stSkeleton stSkeleton--line stSkeleton--w160" />
              <div className="stSkeleton stSkeleton--line stSkeleton--w220" />
            </div>
            <div className="stSkeleton stSkeleton--pill" />
          </div>

          <div className="stAccountDivider" />

          <div className="stAccountSection">
            <div className="stSkeleton stSkeleton--line stSkeleton--w140" />
            <div className="stSkeleton stSkeleton--line stSkeleton--w260" />
            <div className="stSkeleton stSkeleton--line stSkeleton--w240" />
            <div className="stSkeleton stSkeleton--line stSkeleton--w280" />
          </div>
        </div>
      )
    }

    if (!accountUser) {
      return (
        <div className="stAccountCard">
          <div className="stAccountSection">
            <div className="stSectionTitle">{tr('accountInfo', 'Account Info')}</div>
            <p className="stEmptyText">
              {tr('accountInfoUnavailable', 'Unable to load account info')}
            </p>
          </div>
        </div>
      )
    }

    const status = accountUser.bannedAt
      ? tr('statusBanned', 'Banned')
      : accountUser.validatedAt
        ? tr('statusVerified', 'Verified')
        : tr('statusNotVerified', 'Not verified')

    const statusClass = accountUser.bannedAt
      ? 'isBanned'
      : accountUser.validatedAt
        ? 'isVerified'
        : 'isNotVerified'

    const basicRows: Array<{ label: string; value: string }> = [
      { label: tr('name', 'Name'), value: accountUser.name || '—' },
      { label: tr('email', 'Email'), value: accountUser.email || '—' },
      { label: tr('phone', 'Phone'), value: accountUser.phone || '—' },
      { label: tr('identifier', 'Identifier'), value: accountUser.identifier || '—' },
    ]

    const securityRows: Array<{ label: string; value: string }> = [
      {
        label: tr('registeredAt', 'Registered at'),
        value: formatAccountDate(accountUser.registeredAt),
      },
      {
        label: tr('validatedAt', 'Validated at'),
        value: formatAccountDate(accountUser.validatedAt),
      },
      { label: tr('status', 'Status'), value: status },
    ]

    return (
      <div className="stAccountCard">
        <div className="stAccountHead">
          <div className="stAccountAvatar">
            {getInitials(accountUser.name, accountUser.email)}
          </div>

          <div className="stAccountHeadText">
            <div className="stAccountName">
              {accountUser.name || tr('accountInfo', 'Account Info')}
            </div>
            <div className="stAccountEmail">{accountUser.email || '—'}</div>
            <div className="stAccountHint">
              {tr('accountInfoHint', 'Your personal data and verification status')}
            </div>
          </div>

          <span className={`stStatusBadge ${statusClass}`}>{status}</span>
        </div>

        <div className="stAccountDivider" />

        <div className="stAccountGrid">
          <section className="stAccountSection">
            <div className="stSectionTitle">
              {tr('basicInformation', 'Basic information')}
            </div>
            {basicRows.map((row) => (
              <div className="stInfoRow" key={row.label}>
                <span className="stInfoLabel">{row.label}</span>
                <span className="stInfoValue">{row.value || '—'}</span>
              </div>
            ))}
          </section>

          <section className="stAccountSection">
            <div className="stSectionTitle">
              {tr('accountSecurity', 'Account & security')}
            </div>
            {securityRows.map((row) => (
              <div className="stInfoRow" key={row.label}>
                <span className="stInfoLabel">{row.label}</span>
                <span className="stInfoValue">{row.value || '—'}</span>
              </div>
            ))}
          </section>
        </div>
      </div>
    )
  }

  return (
    <ConfigProvider theme={settingsAntdTheme}>
      <div className={`settingsPage ${themeClass}`} style={pageStyle}>
        <div className="settingsBg" aria-hidden="true" />

        <div className="settingsInner">
          <div className="settingsTop">
            <SearchInput />
          </div>

          <h1 style={{ marginTop: isMobile ? 0 : '28px' }} className="settingsTitle">
            {tr('title', 'Settings')}
          </h1>

          <div className="settingsGrid">
            {/* LEFT */}
            <div className="settingsLeft" ref={leftColRef}>
              <div className="settingsBlock">
                <h2 className="settingsH2">{tr('theme', 'Theme')}</h2>

                <div className="stRowSlot">
                  <Row
                    labelTop={tr('changeThemeLabel', 'CHANGE THEME')}
                    value={themeLabel}
                    mobileTitle={tr('changeTheme', 'Change Theme')}
                    mobileRight={themeLabel}
                    active={themeOpen}
                    onClick={() => {
                      setLangOpen(false)
                      setThemeOpen((v) => !v)
                    }}
                    chevronAccent
                    buttonRef={themeAnchorRef}
                    IconMobile={MdEdit}
                  />

                  {themeOpen && (
                    <div ref={themePopoverRef} className="stPopover" role="dialog">
                      <button
                        type="button"
                        className="stPopRow"
                        onClick={() => applyTheme(THEME_ID.AS_DEVICE)}
                      >
                        <span className="stPopText">
                          {tr('themeDevice', 'Follow to device')}
                        </span>
                        <span
                          className={`stRadioDot ${themeId === THEME_ID.AS_DEVICE ? 'isOn' : ''}`}
                        />
                      </button>

                      <button
                        type="button"
                        className="stPopRow"
                        onClick={() => applyTheme(THEME_ID.DARK)}
                      >
                        <span className="stPopText">{tr('themeDark', 'Dark')}</span>
                        <span
                          className={`stRadioDot ${themeId === THEME_ID.DARK ? 'isOn' : ''}`}
                        />
                      </button>

                      <button
                        type="button"
                        className="stPopRow"
                        onClick={() => applyTheme(THEME_ID.LIGHT)}
                      >
                        <span className="stPopText">{tr('themeLight', 'Light')}</span>
                        <span
                          className={`stRadioDot ${themeId === THEME_ID.LIGHT ? 'isOn' : ''}`}
                        />
                      </button>
                    </div>
                  )}
                </div>

                <div className="stGap" />

                <div className="stRowSlot">
                  <Row
                    labelTop={tr('languageLabel', 'LANGUAGE')}
                    value={languageLabel}
                    mobileTitle={tr('language', 'Language')}
                    mobileRight={languageLabel}
                    active={langOpen}
                    onClick={() => {
                      setThemeOpen(false)
                      setLangOpen((v) => !v)
                    }}
                    chevronAccent
                    buttonRef={langAnchorRef}
                    IconMobile={MdGTranslate}
                  />

                  {langOpen && (
                    <div ref={langPopoverRef} className="stPopover" role="dialog">
                      {LANGUAGE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className="stPopRow"
                          onClick={() => applyLang(opt.value)}
                        >
                          <span className="stPopText">{opt.label}</span>
                          <span
                            className={`stRadioDot ${langCode === opt.value ? 'isOn' : ''}`}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="settingsBlock settingsBlock--spaced">
                <h2 className="settingsH2">{tr('generalInfo', 'General Info')}</h2>

                <Row
                  value={tr('confidentialityAgreement', 'Confidentiality Agreement')}
                  onClick={() => toggleInfo('confidential')}
                  buttonRef={confRef}
                  chevronAccent={activeInfo === 'confidential'}
                  IconMobile={MdShield}
                />

                <Row
                  value={tr('accountInfo', 'Account Info')}
                  onClick={() => toggleInfo('account')}
                  buttonRef={accRef}
                  chevronAccent={activeInfo === 'account'}
                  IconMobile={MdPerson}
                />

                <Row
                  value={tr('contacts', 'Contacts')}
                  onClick={() => toggleInfo('contacts')}
                  buttonRef={contRef}
                  chevronAccent={activeInfo === 'contacts'}
                  IconMobile={MdHeadsetMic}
                />
              </div>

              <button
                type="button"
                className="stLogout"
                onClick={() => dispatch(logout())}
              >
                {tr('logout', 'Logout')}
              </button>
            </div>

            {/* RIGHT (desktop) */}
            <div
              className="settingsRight"
              style={{ paddingBottom: PLAYER_SAFE_SPACE_DESKTOP }}
            >
              {activeInfo && (
                <div className="stRightFloat" style={rightFloatStyle}>
                  {activeInfo === 'confidential' && <>{COUNTRY === 'fr' ? <TermsFR/> : <TermsUA/>}</>}
                  {activeInfo === 'account' && <AccountInfoPanel />}
                  {activeInfo === 'contacts' && <ContactsPanel />}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile overlay */}
        {activeInfo && (
          <div
            className="stMobileOverlay"
            role="dialog"
            aria-modal="true"
            style={mobileOverlayStyle}
          >
            <div className="stMobileHeader">
              <button
                type="button"
                className="stMobileBack"
                onClick={() => setActiveInfo(null)}
                aria-label={tr('back', 'Back')}
              >
                <MdChevronLeft size={22} />
              </button>

              <div className="stMobileHeaderTitle">{infoTitle}</div>
            </div>

            <div className="stMobileBody" style={mobileBodyStyle}>
              {activeInfo === 'confidential' && <>{COUNTRY === 'fr' ? <TermsFR/> : <TermsUA/>}</>}
              {activeInfo === 'account' && <AccountInfoPanel />}
              {activeInfo === 'contacts' && <ContactsPanel />}
            </div>
          </div>
        )}
      </div>
    </ConfigProvider>
  )
}
